import { Editor } from '@tiptap/react';
import React, { useEffect, useRef, useState } from 'react';
import { useSpotlight } from 'src/common/spotlight/use-spotlight.hook';
import { Nullable } from 'src/types/nullable.type';
import { functionNoop } from 'src/utils/function/noop';
import { NeueBasicTextEditor, NeueBasicTextEditorProps } from '../../components/neue-basic-text-editor';
import { MutualActionPlanTextMenu } from './text-menu';

interface Props extends Omit<NeueBasicTextEditorProps, 'onTransaction'> {
  onTransaction?: (editor: any, hasTextSelection: boolean) => void;
  onLinkClick: () => void;
}

export const MutualActionPlanTextInput = ({
  content,
  editable,
  selected,
  onUpdate,
  onTextSize,
  textTypes,
  completed,
  textClasses,
  textPlaceholder,
  focusOnInit,
  focusEnd,
  onTransaction = functionNoop,
  onBackspacePressed,
  onLinkClick,
  onFocus = functionNoop,
}: Props) => {
  const { anchorRef, renderSpotlight } = useSpotlight({
    offset: 0,
    placement: 'top',
    strategy: 'fixed',
    styling: 'tooltip',
    theme: 'journey',
  });
  const [tooltipReference, setTooltipReference] = useState<HTMLElement>();
  const [tooltipContent, setTooltipContent] = useState('');
  const [menuView, setMenuView] = useState<Nullable<'link'>>(null);

  const activeSelectionRef = useRef('');
  const editorRef = useRef<Nullable<Editor>>(null);

  useEffect(() => {
    if (!selected && menuView) {
      setMenuView(null);
    }
  }, [selected, menuView]);

  return (
    <>
      <NeueBasicTextEditor
        content={content}
        editable={editable}
        enableVisitingUrlByCmdClick
        completed={completed}
        onMouseOver={(element) => {
          if (element.nodeName === 'A') {
            setTooltipReference(element);
            anchorRef(element);
            setTooltipContent('Cmd+click to open: ' + (element.getAttribute('href') || ''));
          } else {
            setTooltipReference(undefined);
            anchorRef(null);
          }
          return false;
        }}
        onCreate={(editor) => {
          editorRef.current = editor;
        }}
        selected={selected}
        textTypes={textTypes}
        focusEnd={focusEnd}
        textClasses={textClasses}
        onTransaction={(editor) => {
          if (editorRef.current) {
            const { from, to } = editorRef.current.state.selection;
            onTransaction(editor, from !== to);
          }
        }}
        onBackspacePressed={onBackspacePressed}
        textPlaceholder={textPlaceholder}
        onUpdate={onUpdate}
        focusOnInit={focusOnInit}
        onFocus={onFocus}
        onTextSize={onTextSize}
      />
      {editorRef.current && (
        <MutualActionPlanTextMenu
          activeMenuView={menuView}
          onLinkMenuClose={() => {
            setMenuView(null);
          }}
          onLinkClick={(currentSelection) => {
            activeSelectionRef.current = currentSelection;
            setMenuView('link');
            onLinkClick();
          }}
          editor={editorRef.current}
          showMenu={editable && selected}
        />
      )}
      {renderSpotlight({
        content: tooltipContent,
        show: !!tooltipReference && !!tooltipContent,
      })}
    </>
  );
};
